/**
 * Floating voice-add button.
 *
 * Movable — long-press (or click-and-hold) the button and drag it to any of
 * the four screen corners; it snaps and remembers the corner in localStorage.
 *
 * Tap = record voice via the browser Web Speech API → send transcript to the
 * backend classifier → creates a task in the best-matching column.
 */
import { useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, MicrophoneSlash, Check as CheckIcon, X as XIcon } from "@phosphor-icons/react";

const CORNER_KEY = "atlas:voice-corner"; // one of tr, tl, br, bl
const CORNERS = ["br", "bl", "tr", "tl"];

function loadCorner() {
  try {
    const v = localStorage.getItem(CORNER_KEY);
    if (CORNERS.includes(v)) return v;
  } catch {}
  return "br";
}
function saveCorner(c) { try { localStorage.setItem(CORNER_KEY, c); } catch {} }

function nearestCorner(x, y) {
  const w = window.innerWidth; const h = window.innerHeight;
  const isRight = x > w / 2;
  const isBottom = y > h / 2;
  return `${isBottom ? "b" : "t"}${isRight ? "r" : "l"}`;
}
function positionStyle(corner) {
  const insetX = "16px"; const insetY = "16px";
  const s = { position: "fixed", zIndex: 100 };
  if (corner.startsWith("t")) s.top = insetY; else s.bottom = insetY;
  if (corner.endsWith("r")) s.right = insetX; else s.left = insetX;
  return s;
}

function pickRecognition() {
  const R = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!R) return null;
  const r = new R();
  r.continuous = false;
  r.interimResults = true;
  r.lang = navigator.language || "en-US";
  return r;
}

export default function AtlasVoiceButton({ onCommitted }) {
  const [corner, setCorner] = useState(loadCorner());
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState(null); // {x,y} while dragging
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState(null); // { text, column_key }
  const recogRef = useRef(null);
  const dragStartRef = useRef(null);
  const movedRef = useRef(false);

  const style = pos ? { position: "fixed", zIndex: 100, left: pos.x - 32, top: pos.y - 32 } : positionStyle(corner);

  const onPointerDown = (e) => {
    dragStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    movedRef.current = false;
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 6) {
      setDragging(true);
      movedRef.current = true;
      setPos({ x: e.clientX, y: e.clientY });
    }
  };
  const onPointerUp = (e) => {
    const wasDrag = movedRef.current;
    dragStartRef.current = null;
    if (wasDrag) {
      const nc = nearestCorner(e.clientX, e.clientY);
      setCorner(nc); saveCorner(nc); setPos(null); setDragging(false);
      return;
    }
    setDragging(false);
    setPos(null);
    startVoice();
  };

  const startVoice = () => {
    if (recording || pending) return;
    const r = pickRecognition();
    if (!r) { toast.error("Voice input isn't supported by this browser. Try Chrome or Safari."); return; }
    recogRef.current = r;
    setTranscript("");
    // Set state first so UI shows "Listening..." immediately, then call start
    setRecording(true);
    // Kick off recognition asynchronously so the UI paints the recording
    // state without blocking on the browser's speech engine startup.
    Promise.resolve().then(() => {
      try { r.start(); } catch (e) { /* already started */ }
    });
    r.onresult = (evt) => {
      let txt = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        txt += evt.results[i][0].transcript;
      }
      setTranscript(txt);
    };
    r.onerror = () => { setRecording(false); toast.error("Voice recognition failed"); };
    r.onend = async () => {
      setRecording(false);
      const finalText = (transcriptRef.current || "").trim();
      if (!finalText) return;
      setPending(true);
      try {
        const { data } = await api.post("/atlas/voice/classify", { transcript: finalText });
        setConfirm({ text: data.text || finalText, column_key: data.column_key || "stops" });
      } catch (e) {
        const m = apiErrorMessage(e); if (m) toast.error(m);
      } finally { setPending(false); }
    };
    // Auto-stop after 8s of silence
    setTimeout(() => { try { r.stop(); } catch {} }, 8000);
  };
  const stopVoice = () => {
    try { recogRef.current?.stop(); } catch {}
  };

  // keep a ref of transcript so onend closure can read latest value
  const transcriptRef = useRef("");
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  const commitTask = async () => {
    if (!confirm) return;
    try {
      await api.post("/atlas/tasks", { column_key: confirm.column_key, text: confirm.text, urgency: "green" });
      toast.success("Task added");
      setConfirm(null);
      onCommitted && onCommitted();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  return (
    <>
      <button
        type="button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { dragStartRef.current = null; setDragging(false); setPos(null); }}
        style={style}
        className={`select-none touch-none rounded-full h-16 w-16 sm:h-16 sm:w-16 shadow-lg flex items-center justify-center transition-colors ${
          recording ? "bg-rose-600 text-white animate-pulse" : pending ? "bg-slate-400 text-white" : "bg-[#1f64c2] text-white hover:bg-[#164a97]"
        } ${dragging ? "ring-4 ring-[#327de3]/40" : ""}`}
        title={recording ? "Recording… tap to stop" : "Voice-add task (drag to move)"}
        data-testid="atlas-voice-btn"
      >
        {recording ? <MicrophoneSlash size={26} weight="bold" /> : <Microphone size={26} weight="bold" />}
      </button>

      {recording && (
        <div
          style={positionStyle(corner)}
          className="mb-16 rounded-lg bg-white border border-slate-200 shadow-xl px-3 py-2 text-xs text-slate-700 max-w-[280px]"
          data-testid="atlas-voice-transcript"
        >
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-rose-600 font-semibold">Listening…</span>
            <button onClick={stopVoice} className="ml-auto text-slate-500 hover:text-slate-800 text-[10px] font-bold">STOP</button>
          </div>
          <div className="mt-1 italic text-slate-500 min-h-[20px]">{transcript || "…"}</div>
        </div>
      )}

      {confirm && (
        <div
          className="fixed z-[110] bottom-[104px] right-4 sm:right-6 rounded-xl bg-white border border-slate-200 shadow-2xl p-4 w-[min(360px,calc(100vw-2rem))]"
          data-testid="atlas-voice-confirm"
        >
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
            Add to <span className="text-[#1f64c2]">{confirm.column_key.toUpperCase()}</span>?
          </div>
          <input
            value={confirm.text}
            onChange={(e) => setConfirm({ ...confirm, text: e.target.value })}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-base mb-3 focus:border-[#327de3] focus:outline-none"
            data-testid="atlas-voice-confirm-text"
          />
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Section</div>
            <div className="grid grid-cols-4 gap-1.5">
              {["stops", "brian", "dad", "total", "now", "later", "calls"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setConfirm({ ...confirm, column_key: k })}
                  className={`h-10 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors ${
                    confirm.column_key === k
                      ? "bg-[#1f64c2] text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`atlas-voice-col-${k}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setConfirm(null)}
              className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-sm inline-flex items-center justify-center gap-1"
              data-testid="atlas-voice-cancel"
            >
              <XIcon size={14} weight="bold" /> Cancel
            </button>
            <button
              onClick={commitTask}
              className="flex-[2] h-11 rounded-lg bg-[#1f64c2] hover:bg-[#164a97] text-white text-base font-bold inline-flex items-center justify-center gap-2 shadow-sm"
              data-testid="atlas-voice-save"
            >
              <CheckIcon size={16} weight="bold" /> Add task
            </button>
          </div>
        </div>
      )}
    </>
  );
}
